import { useState, useEffect, useCallback } from 'react';
import { getDisputedReasons } from '@/app/shared/api/endpoints/disputedReasons';
import { DisputedReasons } from '@/app/features/InvoiceBillingReminder/billingReminder.types';
import { option } from '@triumph/design-system/components/Dropdown/Dropdown.types';
import { sortWithValueLast } from '@/app/shared/utils/utils';

interface UseDisputedReasonsOptions {
  enabled?: boolean;
}

interface UseDisputedReasonsReturn {
  disputedReasons: option[];
  isLoading: boolean;
  error: Error | null;
  getDisplayValueById: (id: string | number | null | undefined) => string;
}

let cachedReasons: option[] | null = null;

export const useDisputedReasons = (
  options: UseDisputedReasonsOptions = {}
): UseDisputedReasonsReturn => {
  const { enabled = true } = options;

  const [disputedReasons, setDisputedReasons] = useState<option[]>(cachedReasons || []);
  const [isLoading, setIsLoading] = useState(!cachedReasons);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    if (!enabled) return;

    const fetchReasons = async () => {
      if (cachedReasons) {
        setDisputedReasons(cachedReasons);
        setIsLoading(false);
        return;
      }

      setIsLoading(true);

      try {
        const options: DisputedReasons[] = await getDisputedReasons();
        const mappedOptions: option[] = options.map((item) => ({
          id: item.id.toString(),
          value: item.id.toString(),
          displayValue: item.statusReasonCodeName.toString(),
        }));
        const sortOtherLast = sortWithValueLast(mappedOptions,'displayValue','other'); 
        cachedReasons = sortOtherLast;
        setDisputedReasons(mappedOptions);
      } catch (err) {
        setError(err instanceof Error ? err : new Error('Failed to fetch disputed reasons'));
      } finally {
        setIsLoading(false);
      }
    };

    fetchReasons();
  }, [enabled]);

  const getDisplayValueById = useCallback(
    (id: string | number | null | undefined): string => {
      if (id === null || id === undefined || id === '') return '';

      const stringId = id.toString();
      const matchedReason = disputedReasons.find(r => r.id === stringId || r.value === stringId);

      return matchedReason?.displayValue ?? '';
    },
    [disputedReasons]
  );

  return {
    disputedReasons,
    isLoading,
    error,
    getDisplayValueById,
  };
};
